import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { XMarkIcon, MinusIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useCart } from '../contexts/CartContext';

const API_BASE = import.meta.env.VITE_API_URL || '';

function getImageUrl(path: string) {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  return `${API_BASE}${path.startsWith('/') ? '' : '/'}${path}`;
}

const CartDrawer = () => {
  const { items, isOpen, closeCart, removeItem, updateQuantity, totalPrice, totalItems } = useCart();
  const drawerRef = useRef<HTMLDivElement>(null);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // Trap focus & handle Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeCart();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, closeCart]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="overlay" onClick={closeCart} aria-hidden="true" />

      {/* Drawer */}
      <div
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-label="Shopping cart"
        className="fixed top-0 right-0 bottom-0 w-full max-w-md bg-offwhite z-50 flex flex-col animate-slide-in-right shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-warmgray-200">
          <h2 className="font-display text-lg font-semibold text-offblack">
            Cart ({totalItems})
          </h2>
          <button
            onClick={closeCart}
            className="p-1 text-warmgray-500 hover:text-offblack transition-colors"
            aria-label="Close cart"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto scrollbar-thin px-6 py-4">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="w-16 h-16 rounded-full bg-warmgray-200 flex items-center justify-center mb-6">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-warmgray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                </svg>
              </div>
              <p className="font-sans text-sm text-warmgray-500 mb-6">Your cart is empty</p>
              <button onClick={closeCart} className="btn-secondary btn-sm">
                Continue Shopping
              </button>
            </div>
          ) : (
            <ul className="space-y-6">
              {items.map((item) => (
                <li
                  key={`${item.product._id}-${item.size}-${item.color}`}
                  className="flex gap-4"
                >
                  {/* Thumbnail */}
                  <div className="w-20 h-24 flex-shrink-0 bg-warmgray-200 overflow-hidden">
                    <img
                      src={getImageUrl(item.product.images?.[0] || '')}
                      alt={item.product.name}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  {/* Details */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h3 className="font-sans text-sm font-medium text-offblack truncate">
                          {item.product.name}
                        </h3>
                        <p className="text-xs text-warmgray-400 mt-0.5">
                          {item.size && `Size: ${item.size}`}
                          {item.size && item.color && ' · '}
                          {item.color && `Color: ${item.color}`}
                        </p>
                      </div>
                      <button
                        onClick={() => removeItem(item.product._id, item.size, item.color)}
                        className="p-1 text-warmgray-400 hover:text-red-700 transition-colors flex-shrink-0"
                        aria-label={`Remove ${item.product.name} from cart`}
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>

                    <div className="flex items-center justify-between mt-3">
                      {/* Quantity controls */}
                      <div className="flex items-center border border-warmgray-200">
                        <button
                          onClick={() =>
                            updateQuantity(item.product._id, item.size, item.color, item.quantity - 1)
                          }
                          className="w-8 h-8 flex items-center justify-center text-warmgray-500 hover:text-offblack transition-colors"
                          aria-label="Decrease quantity"
                        >
                          <MinusIcon className="h-3 w-3" />
                        </button>
                        <span className="w-8 h-8 flex items-center justify-center text-xs font-medium text-offblack border-x border-warmgray-200">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() =>
                            updateQuantity(item.product._id, item.size, item.color, item.quantity + 1)
                          }
                          className="w-8 h-8 flex items-center justify-center text-warmgray-500 hover:text-offblack transition-colors"
                          aria-label="Increase quantity"
                        >
                          <PlusIcon className="h-3 w-3" />
                        </button>
                      </div>

                      {/* Price */}
                      <span className="font-sans text-sm font-medium text-offblack">
                        {((item.product.salePrice || item.product.price) * item.quantity).toLocaleString()} DA
                      </span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="border-t border-warmgray-200 px-6 py-5 space-y-4">
            <div className="space-y-2">
              <div className="flex justify-between text-sm font-sans">
                <span className="text-warmgray-500">Subtotal</span>
                <span className="text-offblack">{totalPrice.toLocaleString()} DA</span>
              </div>
              <div className="divider" />
              <div className="flex justify-between font-sans font-medium">
                <span className="text-offblack">Total</span>
                <span className="text-offblack">{totalPrice.toLocaleString()} DA</span>
              </div>
            </div>

            <Link
              to="/checkout"
              onClick={closeCart}
              className="btn-primary w-full text-center block"
            >
              Proceed to Order
            </Link>

            <button
              onClick={closeCart}
              className="w-full text-center text-xs font-sans text-warmgray-500 hover:text-offblack transition-colors uppercase tracking-[0.1em] py-2"
            >
              Continue Shopping
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default CartDrawer;
